import { useEffect, useState } from "react";

import { apis, callApi } from "../api";
import { TProfile } from "../@types";
import { useAccount } from "./useAccount";

export type TProfileService = {
	savedProfile: TProfile | null;
	listeners: (() => void)[];

	addEventListener: (cb: () => void) => void;
	removeEventListener: (cb: () => void) => void;
	saveProfile: (newProfile: TProfile) => void;
	updateProfile: (partialProfile: Partial<TProfile>) => void;
	emitChanges: () => void;
	clearData: () => void;
};

export const ProfileService: TProfileService = {
	savedProfile: null,
	listeners: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	saveProfile(newProfile) {
		this.savedProfile = { ...(this.savedProfile ?? {}), ...newProfile };
		this.emitChanges();
	},

	updateProfile(partialProfile) {
		if (this.savedProfile) {
			this.savedProfile = { ...this.savedProfile, ...partialProfile };
			this.emitChanges();
		}
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},

	clearData() {
		this.savedProfile = null;
		this.emitChanges();
	},
};

export function useProfileInit() {
	const { account } = useAccount();
	useEffect(() => {
		const getProfile = async () => {
			try {
				const response = await callApi({
					method: "GET",
					url: apis.PROFILE,
				});

				ProfileService.saveProfile(response);
			} catch (error) {}
		};

		if (account) {
			getProfile();
		}
	}, [account]);
}

export function useProfile() {
	const [profile, setProfile] = useState(ProfileService.savedProfile);
	const { account } = useAccount();

	useEffect(() => {
		const updateProfile = () => {
			setProfile(ProfileService.savedProfile);
		};

		ProfileService.addEventListener(updateProfile);

		return () => {
			ProfileService.removeEventListener(updateProfile);
		};
	}, []);

	const updateProfile = async (data: Partial<TProfile>) => {
		try {
			const response = await callApi({
				method: "PATCH",
				url: apis.PROFILE,
				body: data,
			});

			if (!response) {
				return;
			}

			ProfileService.updateProfile(data);
		} catch (error) {}
	};

	const uploadProfilePicture = async (file: File) => {
		try {
			if (!account || !profile) {
				return;
			}

			const getSignedURLResponse = await callApi({
				method: "POST",
				url: apis.GET_SIGNED_URL,
				body: {
					fileName: file.name,
					docType: "profile",
					contentType: file.type,
					userId: account.userId,
				},
			});

			if (!getSignedURLResponse) {
				return;
			}

			const { signedURL, location } = getSignedURLResponse;
			await callApi({
				method: "PUT",
				baseURL: "",
				url: signedURL,
				body: file,
			});

			const uploadPictureResponse = await callApi({
				method: "POST",
				url: apis.PROFILE_PICTURE,
				body: {
					location,
				},
			});

			if (!uploadPictureResponse?.profile) {
				return;
			}

			ProfileService.saveProfile(uploadPictureResponse.profile);
		} catch (error) {}
	};

	const deleteProfilePicture = async () => {
		if (!ProfileService.savedProfile) {
			return;
		}

		try {
			await callApi({
				method: "DELETE",
				url: apis.PROFILE_PICTURE,
			});

			ProfileService.saveProfile({
				...ProfileService.savedProfile,
				url: "",
				location: "",
			});
		} catch (error) {}
	};

	return {
		profile,
		updateProfile,
		uploadProfilePicture,
		deleteProfilePicture,
	};
}
